import { ChangeEvent, useContext } from "react";
import { v4 as uuidv4 } from "uuid";
import { POSTHOG_EVENTS } from "../../constants/PostHogEvents";
import { usePostHog } from "../../hooks/usePostHog.tsx";
import { useAppStore } from "../../hooks/useStores.tsx";
import { CampaignEntityStatus } from "../../models/common.ts";
import useCampaignHeaderUtils from "../CustomUtils/CampaignHeaderUtils.tsx";
import useDesignEditorUtils from "../CustomUtils/UseDesignEditor.tsx";
import useTemplateUtils from "../CustomUtils/UseTemplateUtils.tsx";
import { DesignEditorContext } from "../contexts/DesignEditor.tsx";
import { BrandDefinition } from "../models/brandDefinition.ts";
import { Image } from "../models/image.ts";
import { ILayer, IScene } from "../types";
import {
	CampaignApiMainObject,
	CampaignApiParameters,
	CampaignCustomOptions,
	CampaignResponseMessages,
	LayerImage,
	LayerText,
	SmartImageBackgroundOperation,
} from "../types/campaign.ts";
import {
	BackgroundOptionsEnum,
	BackgroundRoute,
	ISmartImageParams,
	ObjectsEnum,
} from "../views/DesignEditor/components/Panels/panelItems";
import { useSmartImageGeneration, useSmartImageUtils } from "../views/DesignEditor/utils/smartImageUtils.ts";
import { useEditor } from "./useEditor.tsx";

const useCampaignAPI = () => {
	const editor = useEditor();
	const { imagesStore } = useAppStore();
	const { trackEvent } = usePostHog();
	const { loadTemplate } = useTemplateUtils();
	const {
		uploadAndRegisterBackgroundImages,
		updateLayerStaticContent,
		getSmartImageSize,
		findObjectFromScene,
		drawSmartImage,
		resetRectangleInAGroup,
	} = useSmartImageUtils();
	const { buildUpdatedTemplate } = useCampaignHeaderUtils();
	const { generateCampaignSmartImages } = useSmartImageGeneration();
	const { applyBrandConfigrationOnScene } = useDesignEditorUtils();
	const { setScenes, setCurrentScene } = useContext(DesignEditorContext);
	const { designEditorStore, brandsDefinitionStore, campaignStore } = useAppStore();
	const pushResponseMessage = (status: string, message: string, template?: any) => {
		const response: any = {
			type: "campaign_response",
			status: status,
			campaign: {},
		};

		if (status === "failed") {
			response.campaign.message = message;
		} else {
			response.campaign.template = template;
		}

		window.messages.push(response);
	};

	const updateEditorStep = async (step: number, delay: number = 1000) => {
		await new Promise((resolve) => setTimeout(resolve, delay));
		designEditorStore.setProperty("newStep", step);
	};

	const isValidHexColor = (value: string) => {
		if (value === "") return true;
		const hexColorRegex = /^#([0-9A-F]{3}|[0-9A-F]{6})$/i;
		return hexColorRegex.test(value);
	};

	const setCampaignApiConfigs = async (campaign: CampaignApiParameters): Promise<IScene | undefined> => {
		designEditorStore.setProperty("isTemplateEmptyState", false);
		designEditorStore.setProperty("isLoadingTemplate", false);
		if (
			campaign.mainObject.operation === SmartImageBackgroundOperation.Generate &&
			campaign.mainObject.backgroundPrompt
		) {
			imagesStore.handleSmartImageChange("backgroundOption", BackgroundOptionsEnum.REPLACE_BACKGROUND);
			if (isValidHexColor(campaign.mainObject.backgroundPrompt)) {
				imagesStore.handleSmartImageChange("colorCode", campaign.mainObject.backgroundPrompt as string);
			} else {
				imagesStore.handleSmartImageChange("backgroundDescription", campaign.mainObject.backgroundPrompt);
			}
		} else {
			imagesStore.handleSmartImageChange("backgroundOption", BackgroundOptionsEnum.EXPAND_BACKGROUND);
		}
		await updateEditorStep(1, 4000);
		return editor?.scene?.exportToJSON();
	};

	const drawOriginalAndSmartImages = async (image: Image, externalScene?: IScene) => {
		try {
			if (editor) {
				const _currentScene = externalScene ?? editor.scene.exportToJSON();
				const originalImage = await findObjectFromScene(_currentScene, ObjectsEnum.OriginalImage);

				if (
					!originalImage ||
					image.input_params?.originalImage.imageUrl !== imagesStore.smartImageForm.originalImage.imageUrl
				) {
					await uploadAndRegisterBackgroundImages(
						{} as ChangeEvent<HTMLInputElement>,
						image.input_params?.originalImage.imageUrl,
						_currentScene,
					);

					// Setting these states to reflects the smartImage selections
					imagesStore.handleSmartImageChange(
						"backgroundOption",
						(image.input_params as ISmartImageParams).backgroundOption,
					);
					imagesStore.handleSmartImageChange(
						"backgroundDescription",
						(image.input_params as ISmartImageParams).backgroundDescription,
					);
					imagesStore.handleSmartImageChange(
						"colorCode",
						(image.input_params as ISmartImageParams).colorCode,
					);
					imagesStore.handleSmartImageChange(
						"originalImage",
						(image.input_params as ISmartImageParams).originalImage,
					);
				}
				await drawSmartImage(image, _currentScene);
				if (image?.input_params?.backgroundOption === BackgroundOptionsEnum.EXPAND_BACKGROUND) {
					imagesStore.handleSmartImageChange("backgroundDescription", "");
				}
				designEditorStore.setProperty("isSceneFullyLoaded", false);
			}
		} catch (error) {
			console.error("Error processing smart image:", error);
		}
	};

	const applyBrandOnScene = async (currentScene: IScene, brand: BrandDefinition, sceneIndex: number) => {
		if (editor && brand) {
			brandsDefinitionStore.setProperty("globalSelectedBrand", brand);
			brandsDefinitionStore.setProperty("isApplyingBrandConfigurations", true);
			await applyBrandConfigrationOnScene(brand, currentScene, sceneIndex);
			brandsDefinitionStore.setProperty("isApplyingBrandConfigurations", false);
			setCurrentScene(currentScene);
			campaignStore.isFirstTimeBrandApplied = false;
		}
	};

	const processSmartImageBackground = async (currentScene: IScene, mainObject: CampaignApiMainObject) => {
		await updateEditorStep(2);
		if (mainObject.skip.toLowerCase() === "false") {
			await uploadAndRegisterBackgroundImages({} as ChangeEvent<HTMLInputElement>, mainObject.url, currentScene);
			if (mainObject.operation === SmartImageBackgroundOperation.Expand) {
				const smartImageSize = await getSmartImageSize(currentScene);
				if (smartImageSize) {
					await imagesStore.generateOrExpandImageBackground(BackgroundRoute.Expand, smartImageSize);
					await drawOriginalAndSmartImages(imagesStore.smartImagesHistory[0], currentScene);
				}
			} else if (mainObject.backgroundPrompt && mainObject.operation === SmartImageBackgroundOperation.Generate) {
				const smartImageSize = await getSmartImageSize(currentScene);
				const localSmartImageForm: ISmartImageParams = {
					...imagesStore.smartImageForm,
					colorCode: isValidHexColor(mainObject.backgroundPrompt) ? mainObject.backgroundPrompt : "",
					backgroundDescription: !isValidHexColor(mainObject.backgroundPrompt)
						? mainObject.backgroundPrompt
						: "",
				};
				imagesStore.setProperty("smartImageForm", localSmartImageForm);
				imagesStore.handleSmartImageChange("backgroundOption", BackgroundOptionsEnum.REPLACE_BACKGROUND);
				if (smartImageSize) {
					try {
						await imagesStore.generateOrExpandImageBackground(
							BackgroundRoute.Replace,
							smartImageSize,
							localSmartImageForm,
						);
					} catch (e) {
						if (e === "Error expanding/generating background: Request failed with status code 400") {
							pushResponseMessage("failed", "Offensive content detected in the background prompt.");
						} else {
							throw e;
						}
					}
					await drawOriginalAndSmartImages(imagesStore.smartImagesHistory[0], currentScene);
				}
			}
		}
		// TODO: The delay is not the best solution here, I will try to remove/minimize it after the int deployment
		await updateEditorStep(3, 6000);
	};

	const getMainSceneIndex = (scenes: IScene[]) => {
		const mainSceneIndex = scenes.findIndex((scene) => scene.isMainScene);
		return mainSceneIndex != -1 ? mainSceneIndex : 0;
	};

	const handleStaticContentChanges = async (
		l_scenes: IScene[],
		currentScene: IScene,
		backgroundColor: string,
		texts: LayerText[],
		images: LayerImage[],
	) => {
		if (editor) {
			const updatedLayers = await Promise.all(
				currentScene.layers.map(async (layer) => {
					const updatedLayer = await updateLayerStaticContent(layer, {
						backgroundColor,
						texts,
						images,
					} as CampaignCustomOptions);

					return updatedLayer ? updatedLayer : null;
				}),
			);
			currentScene.layers = updatedLayers.filter((layer) => layer !== null) as Partial<ILayer>[];

			const updatedPreview = (await editor?.renderer.render(currentScene)) as string;
			const updatedScenes = l_scenes.map((scene: IScene) => {
				if (scene.id === currentScene.id) {
					return { ...currentScene, preview: updatedPreview };
				}
				return editor.scene.formalizeSceneAttributes(scene);
			}) as IScene[];
			setScenes(updatedScenes);
			const mainSceneIndex = getMainSceneIndex(updatedScenes);
			setCurrentScene(updatedScenes[mainSceneIndex]);
			return updatedScenes;
		}
		return l_scenes;
	};

	const generateCampaign = async (l_scenes: IScene[], options: CampaignCustomOptions) => {
		const campaignName = uuidv4();
		designEditorStore.setProperty("campaignName", campaignName);
		designEditorStore.setProperty("isGenerateAdsLoading", true);

		try {
			let updatedScenes = await generateCampaignSmartImages(l_scenes, options);

			if (!updatedScenes || updatedScenes.length < 0) {
				return;
			}
			const finalScenes = await Promise.all(
				updatedScenes?.map(async (scene: IScene) => {
					const updatedPreview = (await editor?.renderer.render(scene)) as string;
					return { ...scene, preview: updatedPreview };
				}),
			);

			setScenes(finalScenes);
			const mainSceneIndex = getMainSceneIndex(finalScenes);
			setCurrentScene(finalScenes[mainSceneIndex]);
			pushResponseMessage("success", "", finalScenes);
			if (options.id) saveCampign(finalScenes, options.id);
			designEditorStore.setProperty("hideCampaignResultContent", false);

			// Track campaign created
			trackEvent(POSTHOG_EVENTS.CAMPAIGN_CREATED, {
				campaign_name: campaignName,
				ad_count: finalScenes.length,
				send_instantly: true,
			});
		} catch (error: any) {
			// Track campaign creation failed
			trackEvent(POSTHOG_EVENTS.CAMPAIGN_CREATION_FAILED, {
				error: error?.message || error?.toString() || "Unknown error",
				send_instantly: true,
			});

			throw error; // Re-throw to maintain existing error handling
		}
	};

	const handleCampaignApiRequest = async (campaign: CampaignApiParameters): Promise<void> => {
		try {
			campaignStore.setProperty("selectedWizardTemplate", campaign.template);

			await loadTemplate(campaign.template.ads_json);
			const currentScene = await setCampaignApiConfigs(campaign);
			if (!currentScene) {
				throw new Error("Current scene export failed.");
			}

			const mainSceneIndex = getMainSceneIndex(campaign.template.ads_json.scenes);
			if (campaign.template.is_branded_template) {
				await applyBrandOnScene(currentScene, campaign.brand, mainSceneIndex);
			}
			await processSmartImageBackground(currentScene, campaign.mainObject);
			const skip = campaign.mainObject.skip.toLowerCase() === "true";
			if (skip) {
				await resetRectangleInAGroup(currentScene, ObjectsEnum.OuterRectangle);
				await resetRectangleInAGroup(currentScene, ObjectsEnum.InnerRectangle);
			}
			const updatedScenes = await handleStaticContentChanges(
				campaign.template.ads_json.scenes,
				currentScene,
				campaign.backgroundColor,
				campaign.texts,
				campaign.images,
			);
			await updateEditorStep(6, 5000);
			await generateCampaign(updatedScenes, {
				backgroundColor: campaign.backgroundColor,
				texts: campaign.texts,
				images: campaign.images,
				skip: skip,
				id: campaign.id,
			});
		} catch (error) {
			console.error(CampaignResponseMessages.SomethingWentWrong, error);
			pushResponseMessage("failed", CampaignResponseMessages.SomethingWentWrong);
			designEditorStore.setProperty("isLoadingTemplate", false);
		}
	};
	/**
	 * Saves the generated ads from the Campaign API into the database.
	 *
	 * This method updates the campaign template with the generated scenes and
	 * stores it in the DB so that the user can later access each ad
	 * through the editor iframe link returned in the API response.
	 *
	 * @async
	 * @function saveCampaign
	 * @param {IScene[]} scenes - The list of generated ad scenes to be saved.
	 * @param {string | number} id - The unique campaign ID associated with the template.
	 * @returns {Promise<void>} Resolves when the campaign and its scenes are successfully saved.
	 */
	const saveCampign = async (scenes: IScene[], id: any) => {
		const updatedTemplate = await buildUpdatedTemplate(scenes, designEditorStore.campaignName);
		if (updatedTemplate) {
			updatedTemplate.id = id;
			updatedTemplate.status = CampaignEntityStatus.DRAFT;
		}
		if (updatedTemplate) {
			campaignStore.selectedTemplate = updatedTemplate;
			await campaignStore.updateTemplate(updatedTemplate);
		}
	};

	return {
		pushResponseMessage,
		handleCampaignApiRequest,
	};
};

export default useCampaignAPI;
